import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Heart, Maximize2, Layers } from "lucide-react";
import SEO from "@/components/site/SEO";
import Lightbox from "@/components/site/Lightbox";
import { Button } from "@/components/ui/button";
import { useBoard } from "@/context/BoardContext";
import api from "@/lib/api";

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const { has, toggle } = useBoard();

  useEffect(() => { api.get("/gallery").then((r) => setItems(r.data)).catch(() => {}); }, []);

  const categories = ["all", ...Array.from(new Set(items.map((i) => i.category)))];
  const filtered = (filter === "all" ? items : items.filter((i) => i.category === filter))
    .slice().sort((a, b) => (b.featured === true) - (a.featured === true));

  const openAt = (id) => setLightboxIndex(filtered.findIndex((i) => i.id === id));

  return (
    <div>
      <SEO title="Project Gallery | CG Concrete LLC — Watertown, WI" description="Browse real concrete driveways, patios, walkways, and commercial slabs completed by CG Concrete LLC across the Watertown, WI area. Save your favorites and request a quote." />

      <section className="bg-secondary text-white grain-texture">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <span className="text-primary font-bold text-sm tracking-widest">OUR WORK</span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold mt-3">Project Gallery</h1>
          <p className="text-white/70 mt-5 text-lg max-w-2xl">Real projects from real job sites. Tap any photo to view it full-screen — and tap the heart to save it to your Inspiration Board for a faster, more accurate quote.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-wrap gap-2 mb-10 sticky top-20 z-30 bg-background/90 backdrop-blur py-3 -mx-1 px-1">
          {categories.map((c) => (
            <button key={c} onClick={() => setFilter(c)} className={`px-4 py-2 text-sm font-bold border transition-colors ${filter === c ? "bg-primary text-white border-primary" : "border-border hover:border-primary bg-card"}`} data-testid={`gallery-filter-${c}`}>
              {c === "all" ? "All Projects" : c}
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <p className="text-muted-foreground py-20 text-center">No projects in this category yet.</p>
        ) : (
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:balance]">
            {filtered.map((g, i) => {
              const img = g.type === "before_after" ? g.after_image : g.image;
              const saved = has(g.id);
              return (
                <motion.div
                  key={g.id}
                  initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: (i % 3) * 0.05 }}
                  className="mb-5 break-inside-avoid group relative overflow-hidden bg-secondary cursor-pointer"
                  onClick={() => openAt(g.id)}
                  data-testid={`gallery-item-${g.id}`}
                >
                  <img src={img} alt={g.title} className="w-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent opacity-90 group-hover:opacity-100 transition-opacity" />

                  {g.type === "before_after" && (
                    <span className="absolute top-3 left-3 bg-primary text-white text-[10px] font-bold px-2 py-1 tracking-wide flex items-center gap-1"><Layers className="h-3 w-3" /> BEFORE / AFTER</span>
                  )}

                  <button
                    onClick={(e) => { e.stopPropagation(); toggle({ id: g.id, title: g.title, image: img, category: g.category }); }}
                    className={`absolute top-3 right-3 h-9 w-9 rounded-full flex items-center justify-center transition-colors ${saved ? "bg-primary text-white" : "bg-white/90 text-secondary hover:bg-white"}`}
                    data-testid={`gallery-save-${g.id}`}
                    aria-label="Save to board"
                  >
                    <Heart className={`h-4 w-4 ${saved ? "fill-white" : ""}`} />
                  </button>

                  <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                    <div className="flex items-end justify-between gap-2">
                      <div>
                        <h3 className="font-heading text-lg font-bold leading-tight">{g.title}</h3>
                        <p className="text-xs text-white/70 flex items-center gap-1 mt-0.5"><MapPin className="h-3 w-3" /> {g.location}</p>
                      </div>
                      <span className="h-9 w-9 rounded-full bg-white/15 backdrop-blur flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><Maximize2 className="h-4 w-4" /></span>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        <div className="text-center mt-16 bg-card border border-border p-10">
          <h2 className="font-heading text-2xl sm:text-3xl font-extrabold">Like what you see?</h2>
          <p className="text-muted-foreground mt-2">Save your favorites to the Inspiration Board, then send them to us for a tailored quote.</p>
          <Button asChild className="mt-5 bg-primary hover:bg-primary/90 rounded-sm font-bold h-12 px-8"><Link to="/contact">Get Your Free Quote</Link></Button>
        </div>
      </section>

      {lightboxIndex !== null && (
        <Lightbox items={filtered} index={lightboxIndex} onClose={() => setLightboxIndex(null)} onNavigate={setLightboxIndex} />
      )}
    </div>
  );
}
